import type { Ref } from 'vue';
import { unset } from 'lodash-es';
import {
  computed,
  ref,
  toValue,
  unref,
} from 'vue';

import type {
  QueryClient,
  UseMutationOptions,
  UseMutationReturnType,
} from '@tanstack/vue-query';
import { getApp } from '@modules/core/app/utils';
import { isServerValidationError } from '@modules/core/errors';
import prepareApiFormErrors from '@modules/core/utils/prepareApiFormErrors';
import { useToastStore } from '@modules/toast/store';
import {
  useMutation,
  useQueryClient,
} from '@tanstack/vue-query';

import type { IApiErrors, IApiFormErrors } from './types';

type Unref<T> = T extends Ref<infer U> ? Unref<U> : T;

export default function useContextMutation<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>({ showErrorsInToast = true, ...options }: Omit<Unref<UseMutationOptions<TData, TError, TVariables, TContext>>, 'onSuccess'> & {
  showErrorsInToast?: boolean;
  onSuccess?(successOptions: {
    data: TData;
    variables: TVariables;
    context: TContext;
    queryClient: QueryClient;
  }): Promise<void> | void;
}): UseMutationReturnType<TData, TError, TVariables, TContext> & {
  isLoading: Ref<boolean>;
  errors: Ref<IApiFormErrors | undefined>;
  clearError(key: string): void;
  clearAllErrors(): void;
} {
  return getApp().runWithContext(() => {
    const { showErrorsWithToast } = useToastStore();
    const queryClient = useQueryClient();
    const internalErrors: Ref<IApiFormErrors> = ref({});

    const mutation = useMutation<TData, TError, TVariables, TContext>({
      ...options,
      onError: (error, variables, context) => {
        if (isServerValidationError<IApiErrors>(error)) {
          const { errors } = error;
          const { errors: errorsResponse, ...rest } = error.response as { errors: IApiErrors } & IApiErrors;
          internalErrors.value = prepareApiFormErrors({ ...errors, ...rest });
        }

        if (showErrorsInToast) {
          showErrorsWithToast(error);
        }

        return unref(toValue(options)?.onError)?.(error, variables, context);
      },
      onSuccess: (data, variables, context) => {
        internalErrors.value = {};

        return options?.onSuccess?.({
          data,
          variables,
          context,
          queryClient,
        });
      },
    });

    const errors = computed(() => (
      mutation.isError.value ? internalErrors.value : undefined
    ));

    return {
      ...mutation,
      errors,
      clearError: (key: string): void => {
        if (internalErrors.value[key]) {
          delete internalErrors.value[key];
        }

        if (key.includes('.')) {
          unset(internalErrors.value, key);
        }
      },
      clearAllErrors: (): void => {
        internalErrors.value = {};
      },
      /**
       * Legacy alias for backward compatibility.
       *
       * TanStack Query v5 renamed `isLoading` → `isPending`.
       * In our project, we still use `isLoading` in many places.
       * To avoid massive refactors right now, we map:
       *
       *   isLoading (project API) = mutation.isPending (TanStack API)
       *
       *   https://tanstack.com/query/latest/docs/framework/vue/guides/migrating-to-v5#status-loading-has-been-changed-to-status-pending-and-isloading-has-been-changed-to-ispending-and-isinitialloading-has-now-been-renamed-to-isloading
       *
       * Please use `isPending` in all **new code**.
       * TODO: This alias will be removed once the migration is complete.
       */
      isLoading: mutation.isPending,
    };
  });
}
